import { useEffect, useRef, useState } from 'react'
import styles from './WhoThisIsFor.module.css'

// The profile of someone who will recognise themselves here
const signals = [
  "You've shipped code that looked right and wasn't.",
  'You can read the seam between AI output and your own.',
  "You've fixed a suggestion instead of using it.",
  'You want help that meets your standard, not help that lowers it.',
  "You're not anti-AI. You're anti-wrong.",
]

const WhoThisIsFor = () => {
  const sectionRef = useRef<HTMLElement>(null)
  const signalRefs = useRef<(HTMLLIElement | null)[]>([])
  const [visibleSignals, setVisibleSignals] = useState<boolean[]>(
    new Array(signals.length).fill(false)
  )
  const [headerVisible, setHeaderVisible] = useState(false)
  const [bodyVisible, setBodyVisible] = useState(false)
  const [closingVisible, setClosingVisible] = useState(false)
  const bodyRef = useRef<HTMLDivElement>(null)
  const closingRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    const observers: IntersectionObserver[] = []

    // Header
    const headerObs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setHeaderVisible(true) },
      { threshold: 0.25 }
    )
    if (sectionRef.current) headerObs.observe(sectionRef.current)
    observers.push(headerObs)

    // Body paragraph block
    const bodyObs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setBodyVisible(true) },
      { threshold: 0.2 }
    )
    if (bodyRef.current) bodyObs.observe(bodyRef.current)
    observers.push(bodyObs)

    // Closing
    const closingObs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setClosingVisible(true) },
      { threshold: 0.3 }
    )
    if (closingRef.current) closingObs.observe(closingRef.current)
    observers.push(closingObs)

    // Individual signal items
    signalRefs.current.forEach((el, i) => {
      if (!el) return
      const obs = new IntersectionObserver(
        ([e]) => {
          if (e.isIntersecting) {
            setVisibleSignals(prev => {
              const next = [...prev]
              next[i] = true
              return next
            })
          }
        },
        { threshold: 0.5 }
      )
      obs.observe(el)
      observers.push(obs)
    })

    return () => observers.forEach(o => o.disconnect())
  }, [])

  return (
    <section ref={sectionRef} id="who-this-is-for" className={styles.section}>

      {/* Background: deep right-side glow */}
      <div className={styles.bgGlow} />

      {/* Vertical rule — decorative thread running through the section */}
      {/* <div className={styles.vertRule} /> */}

      <div className="container">
        <div className={styles.layout}>

          {/* Sticky label column */}
          <div className={styles.labelColumn}>
            <div
              className={styles.sectionTag}
              style={{
                opacity: headerVisible ? 1 : 0,
                transform: headerVisible ? 'translateY(0)' : 'translateY(16px)',
                transition: 'opacity 0.8s ease, transform 0.8s ease',
              }}
            >
              <span className={styles.tagNumber}>06</span>
              <span className={styles.tagLine} />
              <span className={styles.tagText}>Who This Is For</span>
            </div>
          </div>

          {/* Content column */}
          <div className={styles.contentColumn}>

            {/* Opening statement — large */}
            <div
              className={styles.openingBlock}
              style={{
                opacity: headerVisible ? 1 : 0,
                transform: headerVisible ? 'translateY(0)' : 'translateY(24px)',
                transition: 'opacity 0.9s ease 0.1s, transform 0.9s ease 0.1s',
              }}
            >
              <p className={styles.openingLarge}>
                Have you ever been the person who accepted a suggestion, shipped
                it, and spent the next morning figuring out why something that{' '}
                <em className={styles.openingEmphasis}>looked fine</em>{' '}
                doesn't quite work?
              </p>
            </div>

            {/* Body paragraphs */}
            <div
              ref={bodyRef}
              className={styles.bodyBlock}
            >
              <p
                className={styles.bodyPara}
                style={{
                  opacity: bodyVisible ? 1 : 0,
                  transform: bodyVisible ? 'translateY(0)' : 'translateY(16px)',
                  transition: 'opacity 0.8s ease, transform 0.8s ease',
                }}
              >
                You've learned to read the seams where AI-generated code joins your own,
                that slight change in logic, in style, in intent. The place where it
                stops being <em>yours</em> and starts being something you're managing.
              </p>

              <p
                className={styles.bodyPara}
                style={{
                  opacity: bodyVisible ? 1 : 0,
                  transform: bodyVisible ? 'translateY(0)' : 'translateY(16px)',
                  transition: 'opacity 0.8s ease 0.12s, transform 0.8s ease 0.12s',
                }}
              >
                You're not against AI assistance. You're against assistance that
                creates more work than it saves: suggestions that technically compile
                but don't fit. Help that requires you to undo it, explain it, or
                quietly replace it with what you meant in the first place.
              </p>

              <p
                className={styles.bodyPara}
                style={{
                  opacity: bodyVisible ? 1 : 0,
                  transform: bodyVisible ? 'translateY(0)' : 'translateY(16px)',
                  transition: 'opacity 0.8s ease 0.22s, transform 0.8s ease 0.22s',
                }}
              >
                This is built for developers who have a standard for their own work
                and need something that can meet them there. Not something they have
                to manage around.
              </p>
            </div>

            {/* Recognition signals — the "you might be this person if..." list */}
            <div className={styles.signalsBlock}>
              <p className={styles.signalsLabel}>If any of these land, you're who we built this to help:</p>
              <ul className={styles.signalsList}>
                {signals.map((signal, i) => (
                  <li
                    key={i}
                    ref={el => signalRefs.current[i] = el}
                    className={styles.signalItem}
                    style={{
                      opacity: visibleSignals[i] ? 1 : 0,
                      transform: visibleSignals[i] ? 'translateX(0)' : 'translateX(-20px)',
                      transition: `opacity 0.6s ease ${i * 0.07}s, transform 0.6s ease ${i * 0.07}s`,
                    }}
                  >
                    <span className={styles.signalTick}>✓</span>
                    <span>{signal}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Closing — the invitation */}
            <p
              ref={closingRef}
              className={styles.closing}
              style={{
                opacity: closingVisible ? 1 : 0,
                transform: closingVisible ? 'translateY(0)' : 'translateY(16px)',
                transition: 'opacity 0.9s ease, transform 0.9s ease',
              }}
            >
              If you've ever caught yourself fixing a suggestion instead of using it,
              you already understand the problem we're solving.{' '}
              <span className={styles.closingCta}>
                That's exactly the gap we are closing.
              </span>
            </p>

          </div>
        </div>
      </div>
    </section>
  )
}

export default WhoThisIsFor
